import { useEffect, useState, FunctionComponent, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import moment from 'moment';
import { TransferFundRequest } from './types';
import TransferFundForm from './TransferFundForm';
import { TransferFundCtx } from '../Dialogue/TransferFund/TransferFundContext';
import { TransferFundDialogueHandler, DialogueHandlerProps } from '../Dialogue/types';
import { useNavMenu } from '../../../providers/NavMenu.provider';

import usePostAccountTransferTo from '../../../hooks/useApi/usePostAccountTransferTo';
import usePostAccountTransferFund from '../../../hooks/useApi/usePostAccountTransferFund';
import usePostAccountTransferWithdraw from '../../../hooks/useApi/usePostAccountTransferWithdraw';
import usePostAccountTransferFundWithdraw from '../../../hooks/useApi/usePostAccountTransferFundWithdraw';

import usePostAccountTransferFundSchedule from '../../../hooks/useApi/usePostAccountTransferFundSchedule';
import usePostAccountTransferWithdrawSchedule from '../../../hooks/useApi/usePostAccountTransferWithdrawSchedule';
import usePostAccountTransferFundWithdrawSchedule from '../../../hooks/useApi/usePostAccountTransferFundWithdrawSchedule';
import { useTranslation } from 'react-i18next';
import { Box, Title, sanitizeToNumeric, IsTodayDate } from 'vopay';

type FC = FunctionComponent;

type TokenProps = {
  values: TransferFundRequest;
};

const TransferFundContainer: FC = () => {
  const reviewRef = useRef<HTMLDivElement>(null);
  const history = useHistory();
  const { getModuleTitle } = useNavMenu();
  const { state } = useLocation<TokenProps>();
  const [country, setCountry] = useState('');
  const [isUnderReview, setIsUnderReview] = useState(false);
  const [showTransferFundErrorAlert, setShowTransferFundErrorAlert] = useState(false);
  const [transferFundError, setTransferFundError] = useState('');
  const [showTransferFundConfirmAlert, setShowTransferFundConfirmAlert] = useState(false);
  const [shouldAccountTransferTo, setShouldAccountTransferTo] = useState(false);
  const [shouldAccountTransferWithdraw, setShouldAccountTransferWithdraw] = useState(false);
  const [shouldAccountFundTrnasferWithdraw, setShouldAccountFundTrnasferWithdraw] = useState(false);
  const [canSubmitTransferFundTransaction, setCanSubmitTransferFundTransaction] = useState(false);
  const [shouldAccountTransferFund, setShouldAccountTransferFund] = useState(false);
  const [debitorClientAccount, setDebitorClientAccount] = useState('');
  const [recipientClientAccount, setRecipientClientAccount] = useState('');
  const [debitorClientAccountID, setDebitorClientAccountID] = useState('');
  const [recipientClientAccountID, setRecipientClientAccountID] = useState('');
  const [accountTransferToResponse, postAccountTransferTo] = usePostAccountTransferTo({});
  const [accountTransferFundResponse, postAccountTransferFund] = usePostAccountTransferFund({});
  const [accountTransferWithdrawResponse, postAccountTrasferWithdraw] = usePostAccountTransferWithdraw({});
  const [accountTransferFundWithdrawResponse, postAccountTransferFundWithdraw] = usePostAccountTransferFundWithdraw({});
  const [accountTransferFundScheduleResponse, postAccountTransferFundSchedule] = usePostAccountTransferFundSchedule({});
  const [accountTransferWithdrawScheduleResponse, postAccountTrasferWithdrawSchedule] =
    usePostAccountTransferWithdrawSchedule({});
  const [accountTransferFundWithdrawScheduleResponse, postAccountTransferFundWithdrawSchedule] =
    usePostAccountTransferFundWithdrawSchedule({});
  const { t } = useTranslation('translations', { keyPrefix: 'ClientAccounts.TransferFunds' });

  const [createTransferFundRequest, setCreateTransferFundRequest] = useState<TransferFundRequest>({
    TransferType: '',
    Amount: 0,
    DebitorClientAccountID: '',
    RecipientClientAccountID: '',
    RecipientClientAccountIDSplit: '',
    ClientReferenceNumber: '',
    Notes: '',
    Currency: 'CAD',
    RecipientAccountID: '',
    ParentTransactionID: '',
    IdempotencyKey: '',
    TransactionLabel: '',
    ScheduleType: 'single',
    TakeFromAccountOn: String(Date.now()),
    Frequency: 'Monthly',
    SemiMonthlyDates: '',
    StartDate: String(Date.now()),
    Ending: 'End Date',
    EndDate: '',
    EndingAfterPayments: '',
    Description: '',
  });

  const TransferFundContext: TransferFundCtx = {
    showTransferFundErrorAlert,
    transferFundError,
    showTransferFundConfirmAlert,
  };

  const handlers = {
    handleSubmit: (values: TransferFundRequest) => {
      if (isUnderReview) {
        const recipients = values.RecipientClientAccountID.split('|');
        if (recipients.length === 1 && values.RecipientClientAccountIDSplit !== '') {
          delete values.RecipientClientAccountIDSplit;
        }
        setCreateTransferFundRequest(values);
        setCanSubmitTransferFundTransaction(true);
      } else {
        setIsUnderReview(true);
      }
    },
    handleTransferFundDialogue: (handlerProps: DialogueHandlerProps<TransferFundDialogueHandler>) => {
      const { type, state: stateHandler, action = 'default' } = handlerProps;
      const dialogueHandlers: TransferFundDialogueHandler = {
        error: {
          confirm: {
            default: () => {
              setShowTransferFundErrorAlert(false);
              setCanSubmitTransferFundTransaction(false);
            },
          },
          cancel: {
            default: () => {
              setShowTransferFundErrorAlert(false);
              setCanSubmitTransferFundTransaction(false);
            },
          },
          fail: {
            default: () => {
              setShowTransferFundErrorAlert(false);
              setCanSubmitTransferFundTransaction(false);
            },
          },
        },
        confirm: {
          confirm: {
            default: () => {
              onTransferFund(createTransferFundRequest);
              setShowTransferFundConfirmAlert(false);
            },
          },
          fail: {
            default: () => {
              setShowTransferFundConfirmAlert(false);
              setCanSubmitTransferFundTransaction(false);
            },
          },
          cancel: {
            default: () => {
              setCanSubmitTransferFundTransaction(false);
              setShowTransferFundConfirmAlert(false);
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][stateHandler];
      const handler = dialogueState[action] || dialogueState.default;
      handler();
    },
  };

  useEffect(() => {
    if (shouldAccountTransferTo) {
      setShouldAccountTransferTo(false);
      const { Success, ErrorMessage } = accountTransferToResponse;

      if (Success === true) {
        history.push({
          pathname: '/client-account-transfer-funds/details/transfer',
          state: {
            TransactionID: accountTransferToResponse.TransactionID || '',
            DebitorClientAccount: debitorClientAccount,
            RecipientClientAccount: recipientClientAccount,
            DebitorClientAccountID: debitorClientAccountID,
            RecipientClientAccountID: recipientClientAccountID,
            TransferType: createTransferFundRequest.TransferType,
            values: createTransferFundRequest,
            TransactionLabel: createTransferFundRequest.TransactionLabel || '',
          },
        });
      } else if (Success === false) {
        setShowTransferFundErrorAlert(true);
        setTransferFundError(ErrorMessage);
      }
    }

    if (shouldAccountTransferFund) {
      setShouldAccountTransferFund(false);
      const { Success, ErrorMessage } = accountTransferFundResponse;

      if (Success === true) {
        history.push({
          pathname: '/client-account-transfer-funds/details/fund-transfer',
          state: {
            FundingTransactionID: accountTransferFundResponse.FundingTransactionID || '',
            AccountTransferID: accountTransferFundResponse.AccountTransferID || '',
            AccountTransferIDs: accountTransferFundResponse.AccountTransferIDs || {},
            DebitorClientAccount: debitorClientAccount,
            RecipientClientAccount: recipientClientAccount,
            Country: country,
            values: createTransferFundRequest,
            TransactionLabel: createTransferFundRequest.TransactionLabel || '',
          },
        });
      } else if (Success === false) {
        setShowTransferFundErrorAlert(true);
        setTransferFundError(ErrorMessage);
      }
    }

    if (shouldAccountTransferWithdraw) {
      setShouldAccountTransferFund(false);
      const { Success, ErrorMessage } = accountTransferWithdrawResponse;

      if (Success === true) {
        history.push({
          pathname: '/client-account-transfer-funds/details/withdraw-transfer',
          state: {
            WithdrawTransactionID: accountTransferWithdrawResponse.WithdrawTransactionID || '',
            WithdrawTransactionIDs: accountTransferWithdrawResponse.WithdrawTransactionIDs || {},
            AccountTransferID: accountTransferWithdrawResponse.AccountTransferID || '',
            AccountTransferIDs: accountTransferWithdrawResponse.AccountTransferIDs || {},
            DebitorClientAccount: debitorClientAccount,
            RecipientClientAccount: recipientClientAccount,
            Country: country,
            values: createTransferFundRequest,
            TransactionLabel: createTransferFundRequest.TransactionLabel || '',
          },
        });
      } else if (Success === false) {
        setShowTransferFundErrorAlert(true);
        setTransferFundError(ErrorMessage);
      }
    }

    if (shouldAccountFundTrnasferWithdraw) {
      setShouldAccountTransferFund(false);
      const { Success, ErrorMessage } = accountTransferFundWithdrawResponse;

      if (Success === true) {
        history.push({
          pathname: '/client-account-transfer-funds/details/fund-withdraw-transfer',
          state: {
            FundingTransactionID: accountTransferFundWithdrawResponse.FundingTransactionID || '',
            WithdrawTransactionID: accountTransferFundWithdrawResponse.WithdrawTransactionID || '',
            WithdrawTransactionIDs: accountTransferFundWithdrawResponse.WithdrawTransactionIDs || {},
            AccountTransferID: accountTransferFundWithdrawResponse.AccountTransferID || '',
            AccountTransferIDs: accountTransferFundWithdrawResponse.AccountTransferIDs || {},
            DebitorClientAccount: debitorClientAccount,
            RecipientClientAccount: recipientClientAccount,
            Country: country,
            values: createTransferFundRequest,
            TransactionLabel: createTransferFundRequest.TransactionLabel || '',
          },
        });
      } else if (Success === false) {
        setShowTransferFundErrorAlert(true);
        setTransferFundError(ErrorMessage);
      }
    }
  }, [
    accountTransferToResponse,
    accountTransferFundResponse,
    accountTransferWithdrawResponse,
    accountTransferFundWithdrawResponse,
    state,
  ]);

  useEffect(() => {
    if (canSubmitTransferFundTransaction || state) {
      setDebitorClientAccountID(createTransferFundRequest.DebitorClientAccountID || '');
      // ID split recipients are provided we want to display the created name with all the recipients
      if (createTransferFundRequest.RecipientClientAccountIDSplit === '') {
        setRecipientClientAccountID(createTransferFundRequest.RecipientClientAccountID || '');
      } else {
        setRecipientClientAccount(createTransferFundRequest.RecipientClientAccountID);
        setRecipientClientAccountID(createTransferFundRequest.RecipientClientAccountID || '');
      }
      onTransferFund(createTransferFundRequest);
      setCanSubmitTransferFundTransaction(false);
    }
  }, [canSubmitTransferFundTransaction, state]);

  useEffect(() => {
    if (shouldAccountTransferFund) {
      setShouldAccountTransferFund(false);
      const { Success, ErrorMessage, ScheduledTransfersID } = accountTransferFundScheduleResponse;
      if (Success === true) {
        history.push({
          pathname: `/client-account-schedule/details/${ScheduledTransfersID}`,
        });
      } else if (Success === false) {
        setShowTransferFundErrorAlert(true);
        setTransferFundError(ErrorMessage);
      }
    }
    if (shouldAccountTransferWithdraw) {
      setShouldAccountTransferFund(false);
      const { Success, ErrorMessage, ScheduledTransfersID } = accountTransferWithdrawScheduleResponse;

      if (Success === true) {
        history.push({
          pathname: `/client-account-schedule/details/${ScheduledTransfersID}`,
        });
      } else if (Success === false) {
        setShowTransferFundErrorAlert(true);
        setTransferFundError(ErrorMessage);
      }
    }
    if (shouldAccountFundTrnasferWithdraw) {
      setShouldAccountTransferFund(false);
      const { Success, ErrorMessage, ScheduledTransfersID } = accountTransferFundWithdrawScheduleResponse;

      if (Success === true) {
        history.push({
          pathname: `/client-account-schedule/details/${ScheduledTransfersID}`,
        });
      } else if (Success === false) {
        setShowTransferFundErrorAlert(true);
        setTransferFundError(ErrorMessage);
      }
    }
  }, [
    accountTransferWithdrawScheduleResponse,
    accountTransferFundScheduleResponse,
    accountTransferFundWithdrawScheduleResponse,
  ]);

  useEffect(() => {
    if (isUnderReview) {
      if (reviewRef !== null && reviewRef.current !== null) {
        reviewRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  }, [isUnderReview]);

  return (
    <div ref={reviewRef}>
      <Box mb="md" jc="sb" flx="col" gap="lg">
        {isUnderReview ? <Title title={t('title')} /> : <Title title={getModuleTitle()} />}
        <TransferFundForm
          defaultValues={createTransferFundRequest}
          handlers={handlers}
          TransferFundContext={TransferFundContext}
          isUnderReview={isUnderReview}
          setIsUnderReview={setIsUnderReview}
          setDebitorClientAccount={setDebitorClientAccount}
          setRecipientClientAccount={setRecipientClientAccount}
          setCountry={setCountry}
        />
      </Box>
    </div>
  );

  function onTransferFund(transferFundRequestvalues: TransferFundRequest) {
    const common = {
      Amount: Number(sanitizeToNumeric(String(transferFundRequestvalues.Amount))) || 0,
      DebitorClientAccountID: transferFundRequestvalues.DebitorClientAccountID || '',
      RecipientClientAccountID: transferFundRequestvalues.RecipientClientAccountID || '',
      RecipientClientAccountIDSplit: transferFundRequestvalues.RecipientClientAccountIDSplit || '',
      ClientReferenceNumber: transferFundRequestvalues.ClientReferenceNumber || '',
      Notes: transferFundRequestvalues.Notes || '',
      TransactionLabel: transferFundRequestvalues.TransactionLabel || '',
    };
    const scheduleCommon = {
      Frequency:
        transferFundRequestvalues.ScheduleType === 'single'
          ? 'single'
          : transferFundRequestvalues.Frequency || 'single',
      SemiMonthlyFrequencyType: transferFundRequestvalues.SemiMonthlyDates,
      ScheduleStartDate:
        transferFundRequestvalues.ScheduleType === 'single'
          ? moment(Number(transferFundRequestvalues.TakeFromAccountOn)).format('YYYY-MM-DD')
          : moment(Number(transferFundRequestvalues.StartDate)).format('YYYY-MM-DD'),
      ScheduleEndDate:
        transferFundRequestvalues.Ending === 'End Date' && transferFundRequestvalues.ScheduleType !== 'single'
          ? moment(Number(transferFundRequestvalues.EndDate)).format('YYYY-MM-DD')
          : undefined,
      EndingAfterPayments:
        transferFundRequestvalues.Ending === 'Payments' && transferFundRequestvalues.ScheduleType !== 'single'
          ? Number(transferFundRequestvalues.EndingAfterPayments)
          : undefined,
      Description: transferFundRequestvalues.Description || '',
    };
    const isSchedule =
      transferFundRequestvalues.ScheduleType === 'recurring' ||
      (transferFundRequestvalues.TakeFromAccountOn &&
        !IsTodayDate(moment(Number(transferFundRequestvalues.TakeFromAccountOn)).format('YYYY-MM-DD')));

    if (transferFundRequestvalues.TransferType === 'Transfer') {
      setShouldAccountTransferTo(true);
      postAccountTransferTo({
        ...common,
        Currency: transferFundRequestvalues.Currency,
        RecipientAccountID: '',
        ParentTransactionID: '',
        IdempotencyKey: '',
      });
    } else if (transferFundRequestvalues.TransferType === 'FundAndTransfer') {
      setShouldAccountTransferFund(true);
      if (isSchedule) {
        postAccountTransferFundSchedule({
          ...common,
          Currency: transferFundRequestvalues.Currency,
          ...scheduleCommon,
        });
      } else {
        postAccountTransferFund({
          ...common,
          Currency: transferFundRequestvalues.Currency,
        });
      }
    } else if (transferFundRequestvalues.TransferType === 'TransferAndWithdraw') {
      setShouldAccountTransferWithdraw(true);
      if (isSchedule) {
        postAccountTrasferWithdrawSchedule({
          ...common,
          Currency: transferFundRequestvalues.Currency,
          ...scheduleCommon,
        });
      } else {
        postAccountTrasferWithdraw({
          ...common,
          Currency: transferFundRequestvalues.Currency,
        });
      }
    } else if (transferFundRequestvalues.TransferType === 'FundAndTransferAndWithdraw') {
      setShouldAccountFundTrnasferWithdraw(true);
      if (isSchedule) {
        postAccountTransferFundWithdrawSchedule({
          ...common,
          Currency: transferFundRequestvalues.Currency,
          ...scheduleCommon,
        });
      } else {
        postAccountTransferFundWithdraw({
          ...common,
          Currency: transferFundRequestvalues.Currency,
        });
      }
    }
  }
};

export default TransferFundContainer;
